import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { format, parseISO, isFuture, isPast } from "date-fns";
import { Calendar as CalendarIcon, Clock, Plus, X, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "sonner";

interface ClientAppointmentsProps {
  clientId: string;
  clinicianId: string;
  clinicianClientId: string;
  practitionerType: string;
}

interface Session {
  id: string;
  session_date: string;
  session_time: string;
  duration_minutes: number;
  status: string;
  session_type: string;
  notes: string | null;
}

interface AppointmentRequest {
  id: string;
  request_type: string;
  requested_date: string | null;
  requested_time: string | null;
  reason: string | null;
  status: string;
  clinician_response: string | null;
  created_at: string;
}

export function ClientAppointments({ clientId, clinicianId, clinicianClientId, practitionerType }: ClientAppointmentsProps) {
  // UK date formatting helper
  const formatUKDate = (dateStr: string) => {
    return format(parseISO(dateStr), 'dd-MM-yyyy');
  };
  
  const formatUKLongDate = (dateStr: string) => {
    return format(parseISO(dateStr), "EEEE, d MMMM yyyy");
  };
  const [sessions, setSessions] = useState<Session[]>([]);
  const [requests, setRequests] = useState<AppointmentRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [showNewRequest, setShowNewRequest] = useState(false);
  const [showReschedule, setShowReschedule] = useState<Session | null>(null);
  const [showCancel, setShowCancel] = useState<Session | null>(null);
  
  const [requestForm, setRequestForm] = useState({
    requested_date: '',
    requested_time: '',
    requested_duration: '50',
    reason: ''
  });

  const practitionerLabel = practitionerType === 'coach' ? 'coach' : 'therapist';

  useEffect(() => {
    loadData();
  }, [clientId, clinicianId]);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([loadSessions(), loadRequests()]);
    setLoading(false);
  };

  const loadSessions = async () => {
    const { data, error } = await supabase
      .from('therapy_sessions')
      .select('id, session_date, session_time, duration_minutes, status, session_type')
      .eq('client_id', clientId)
      .eq('clinician_id', clinicianId)
      .order('session_date', { ascending: true });

    if (error) {
      console.error('Error loading sessions:', error);
      return;
    }
    setSessions((data || []).map(s => ({ ...s, notes: null })));
  };

  const loadRequests = async () => {
    const { data, error } = await supabase
      .from('appointment_requests')
      .select('*')
      .eq('client_id', clientId)
      .eq('clinician_id', clinicianId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error loading requests:', error);
      return;
    }
    setRequests(data || []);
  };

  const handleNewRequest = async () => {
    if (!requestForm.requested_date || !requestForm.requested_time) {
      toast.error('Please select a date and time');
      return;
    }

    const { error } = await supabase
      .from('appointment_requests')
      .insert({
        client_id: clientId,
        clinician_id: clinicianId,
        clinician_client_id: clinicianClientId,
        request_type: 'new',
        requested_date: new Date(requestForm.requested_date).toISOString(),
        requested_time: requestForm.requested_time,
        requested_duration: parseInt(requestForm.requested_duration),
        reason: requestForm.reason || null,
        status: 'pending'
      });

    if (error) {
      console.error('Error creating request:', error);
      toast.error('Failed to submit request');
      return;
    }

    toast.success('Appointment request submitted');
    setShowNewRequest(false);
    setRequestForm({ requested_date: '', requested_time: '', requested_duration: '50', reason: '' });
    loadRequests();
  };

  const handleRescheduleRequest = async () => {
    if (!showReschedule || !requestForm.requested_date || !requestForm.requested_time) {
      toast.error('Please select a new date and time');
      return;
    }

    const { error } = await supabase
      .from('appointment_requests')
      .insert({
        client_id: clientId,
        clinician_id: clinicianId,
        clinician_client_id: clinicianClientId,
        request_type: 'reschedule',
        existing_session_id: showReschedule.id,
        requested_date: new Date(requestForm.requested_date).toISOString(),
        requested_time: requestForm.requested_time,
        reason: requestForm.reason || null,
        status: 'pending'
      });

    if (error) {
      console.error('Error creating reschedule request:', error);
      toast.error('Failed to submit request');
      return;
    }

    toast.success('Reschedule request submitted');
    setShowReschedule(null);
    setRequestForm({ requested_date: '', requested_time: '', requested_duration: '50', reason: '' });
    loadRequests();
  };

  const handleCancelRequest = async () => {
    if (!showCancel) return;

    const { error } = await supabase
      .from('appointment_requests')
      .insert({
        client_id: clientId,
        clinician_id: clinicianId,
        clinician_client_id: clinicianClientId,
        request_type: 'cancel',
        existing_session_id: showCancel.id,
        reason: requestForm.reason || null,
        status: 'pending'
      });

    if (error) {
      console.error('Error creating cancel request:', error);
      toast.error('Failed to submit request');
      return;
    }

    toast.success('Cancellation request submitted');
    setShowCancel(null);
    setRequestForm({ requested_date: '', requested_time: '', requested_duration: '50', reason: '' });
    loadRequests();
  };

  const upcomingSessions = sessions.filter(s => 
    isFuture(parseISO(s.session_date)) && s.status !== 'cancelled'
  );

  const pastSessions = sessions.filter(s => 
    isPast(parseISO(s.session_date)) || s.status === 'completed'
  );

  const sessionDates = sessions
    .filter(s => s.status !== 'cancelled')
    .map(s => parseISO(s.session_date));

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500">Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-500/10 text-green-500">Approved</Badge>;
      case 'declined':
        return <Badge variant="outline" className="bg-red-500/10 text-red-500">Declined</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Calendar View */}
      <Card className="bg-card/50 border-border/30">
        <CardHeader>
          <div className="flex items-centre justify-between">
            <div>
              <CardTitle className="flex items-centre gap-2 text-foreground">
                <CalendarIcon className="h-5 w-5 text-primary" />
                My Appointments
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                View your sessions and request new appointments
              </CardDescription>
            </div>
            <Dialog open={showNewRequest} onOpenChange={setShowNewRequest}>
              <DialogTrigger asChild>
                <Button className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90">
                  <Plus className="h-4 w-4" />
                  Request Appointment
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Request New Appointment</DialogTitle>
                  <DialogDescription>
                    Submit a request for a new session with your {practitionerLabel}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Preferred Date</Label>
                    <Input
                      type="date"
                      value={requestForm.requested_date}
                      onChange={(e) => setRequestForm(f => ({ ...f, requested_date: e.target.value }))}
                      min={format(new Date(), 'yyyy-MM-dd')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Preferred Time</Label>
                    <Input
                      type="time"
                      value={requestForm.requested_time}
                      onChange={(e) => setRequestForm(f => ({ ...f, requested_time: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Session Duration</Label>
                    <Select
                      value={requestForm.requested_duration}
                      onValueChange={(v) => setRequestForm(f => ({ ...f, requested_duration: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30">30 minutes</SelectItem>
                        <SelectItem value="50">50 minutes</SelectItem>
                        <SelectItem value="60">60 minutes</SelectItem>
                        <SelectItem value="90">90 minutes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Notes (optional)</Label>
                    <Textarea
                      placeholder="Any additional notes for your request..."
                      value={requestForm.reason}
                      onChange={(e) => setRequestForm(f => ({ ...f, reason: e.target.value }))}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline">Cancel</Button>
                  </DialogClose>
                  <Button onClick={handleNewRequest}>Submit Request</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col lg:flex-row gap-6">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              modifiers={{
                booked: sessionDates
              }}
              modifiersStyles={{
                booked: { backgroundColor: 'hsl(var(--primary) / 0.2)', fontWeight: 'bold' }
              }}
              className="rounded-md border"
            />
            
            <div className="flex-1 space-y-4">
              <h3 className="font-semibold">Upcoming Sessions</h3>
              {upcomingSessions.length === 0 ? (
                <p className="text-muted-foreground text-sm">No upcoming sessions scheduled</p>
              ) : (
                <div className="space-y-3">
                  {upcomingSessions.slice(0, 5).map(session => (
                    <div key={session.id} className="flex items-centre justify-between p-3 bg-card border border-border/30 rounded-lg">
                      <div>
                        <p className="font-medium text-foreground">
                          {formatUKLongDate(session.session_date)}
                        </p>
                        <p className="text-sm text-muted-foreground flex items-centre gap-1">
                          <Clock className="h-3 w-3" />
                          {session.session_time} · {session.duration_minutes} min
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowReschedule(session)}
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowCancel(session)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pending Requests */}
      {requests.filter(r => r.status === 'pending').length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
              Pending Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {requests.filter(r => r.status === 'pending').map(request => (
                <div key={request.id} className="p-3 bg-yellow-500/5 border border-yellow-500/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium capitalize">{request.request_type} Request</p>
                      {request.requested_date && (
                        <p className="text-sm text-muted-foreground">
                          Requested: {format(parseISO(request.requested_date), 'PPP')} at {request.requested_time}
                        </p>
                      )}
                      {request.reason && (
                        <p className="text-sm text-muted-foreground mt-1">{request.reason}</p>
                      )}
                    </div>
                    {getStatusBadge(request.status)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Past Sessions */}
      {pastSessions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Session History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pastSessions.slice(0, 10).map(session => (
                <div key={session.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div>
                    <p className="font-medium">
                      {format(parseISO(session.session_date), 'MMMM d, yyyy')}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {session.session_time} · {session.duration_minutes} min
                    </p>
                  </div>
                  <Badge variant="outline" className="bg-green-500/10 text-green-500">
                    Completed
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reschedule Dialog */}
      <Dialog open={!!showReschedule} onOpenChange={(open) => !open && setShowReschedule(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Reschedule</DialogTitle>
            <DialogDescription>
              Request a new time for your session on {showReschedule && format(parseISO(showReschedule.session_date), 'PPP')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Preferred Date</Label>
              <Input
                type="date"
                value={requestForm.requested_date}
                onChange={(e) => setRequestForm(f => ({ ...f, requested_date: e.target.value }))}
                min={format(new Date(), 'yyyy-MM-dd')}
              />
            </div>
            <div className="space-y-2">
              <Label>New Preferred Time</Label>
              <Input
                type="time"
                value={requestForm.requested_time}
                onChange={(e) => setRequestForm(f => ({ ...f, requested_time: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Textarea
                placeholder="Why do you need to reschedule?"
                value={requestForm.reason}
                onChange={(e) => setRequestForm(f => ({ ...f, reason: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleRescheduleRequest}>Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={!!showCancel} onOpenChange={(open) => !open && setShowCancel(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Cancellation</DialogTitle>
            <DialogDescription>
              Request to cancel your session on {showCancel && format(parseISO(showCancel.session_date), 'PPP')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea
                placeholder="Please provide a reason for cancellation..."
                value={requestForm.reason}
                onChange={(e) => setRequestForm(f => ({ ...f, reason: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Keep Session</Button>
            </DialogClose>
            <Button variant="destructive" onClick={handleCancelRequest}>Submit Cancellation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
